// React
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'

// Libraries
import _get from 'lodash/get'
import _isEqual from 'lodash/isEqual'
import _isNaN from 'lodash/isNaN'

// Components
import BundleItems from './BundleItems'
import ProductPrice from 'store/v2/react/components/product_price'
import InputField from 'ui/components/InputField'
import Button from 'ui/components/Button'
import Icon from 'ui/components/Icon'
import Spacer from 'ui/blocks/Spacer'
import ProductAvailabilityCalendar from 'store/modules/product/components/ProductAvailabilityCalendar'
import ReadOnlyModeDisabledComponent from 'store/components/ReadOnlyModeDisabledComponent'

// Shared
import ReactCSSTransitionGroup from 'shared/lib/react_css_transition_group'
import { triggerUserFrameworkBookEvent } from 'shared/utils/userFrameworkEvents'
import { withTranslation } from 'shared/utils/withTranslation'
import productSelectors from 'store/v2/redux/selectors/product'
import cartActions from 'store/v2/redux/actions/cart'
import clientActions from 'store/v2/redux/actions/client'
import quickviewActions from 'store/v2/redux/actions/quickview'
import lineActions from 'store/v2/redux/actions/line'
import BundleActions from 'store/v2/redux/actions/bundle'
import ProductGroupActions from 'store/v2/redux/actions/product_group'
import { getOperatingRules } from 'shared/utils/operating_rules'
import { handleAxiosError } from 'shared/utils/handleAxiosError'

export class ProductButton extends Component {
  static displayName = 'ProductButton'

  static propTypes = {
    item: PropTypes.object,
    productGroup: PropTypes.object,
    products: PropTypes.object,
    bundle: PropTypes.object,
    line: PropTypes.object,
    cart: PropTypes.object,
    useAvailability: PropTypes.bool,
    showProductAvailability: PropTypes.bool,
    hasDates: PropTypes.bool,
    book: PropTypes.func.isRequired,
    onChangeProductId: PropTypes.func,
    availabilities: PropTypes.object,
    operatingRules: PropTypes.object,
    showCounts: PropTypes.bool,
    t: PropTypes.func,
    clearCart: PropTypes.func,
    loading: PropTypes.bool,
    behavior: PropTypes.string
  }

  constructor (props) {
    super(props)

    this.state = {
      loading: false,
      quantity: 1,
      selectedProductId: null,
      configuration: { products: {} },
      promise: null,
      price_each_in_cents: null,
      available_quantity: null
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    const cartChanged = prevProps.cart?.updated_at !== this.props.cart?.updated_at

    const isBundle = this.props.item.type === 'Bundle'
    const bundleConfigChanged = !_isEqual(prevState.configuration, this.state.configuration)

    if (cartChanged) {
      this.loadItemData()
    }
    if (isBundle && (cartChanged || bundleConfigChanged)) {
      this.loadBundlePriceAndAvailability()
    }
  }

  get selectedProduct () {
    const { products, item } = this.props
    const { selectedProductId } = this.state

    if (item.type !== 'ProductGroup') return null

    return productSelectors.selectedForProductGroup(products, selectedProductId, item.id)
  }

  loadItemData = () => {
    const { item, cart } = this.props

    this.setState({ loading: true })

    if (item.type === 'ProductGroup') {
      return ProductGroupActions.fetchAll({
        ids: [item.id],
        cart_id: cart && cart.id,
        include: 'photos,products',
        apiVersion: 3
      })
        .then(() => this.setState({ loading: false, price_each_in_cents: null }))
        .catch(handleAxiosError(this.props.t))
    } else if (item.type === 'Bundle') {
      return BundleActions.fetchAll({
        ids: [item.id],
        cart_id: cart && cart.id,
        include: [
          'photos',
          'bundle_items',
          'bundle_items.product',
          'bundle_items.product.photo',
          'bundle_items.product_group',
          'bundle_items.product_group.products',
          'bundle_items.product_group.photo',
          'bundle_items.product_group.products',
          'bundle_items.product_group.products.photo'
        ].join(','),
        apiVersion: 3
      })
        .then(() => this.setState({ loading: false, price_each_in_cents: null }))
        .catch(handleAxiosError(this.props.t))
    }
  }

  checkoutAction = () => {
    const { hasDates } = this.props
    if (clientActions.evaluateCartOverdue() || !hasDates) {
      return clientActions.toggleModalPicker(true)
    }

    quickviewActions.unsetItem()
    clientActions.setCheckingOut()
  }

  showCartAction = () => {
    quickviewActions.unsetItem()
    clientActions.toggleCart(true)
  }

  handleBook = async () => {
    const { book, behavior, line, item, cart, clearCart } = this.props
    const { quantity, selectedProductId, configuration } = this.state

    const itemId = selectedProductId || _get(this.selectedProduct, 'id') || item.id

    this.setState({ loading: true })

    if (behavior === 'checkout' && cart?.created_at) await clearCart()

    let promise = book(itemId, quantity, configuration)
      .then(() => {
        if (line) lineActions.addLineQtyUI(line, quantity)

        const action = behavior === 'show_cart' ? this.showCartAction : this.checkoutAction

        triggerUserFrameworkBookEvent(itemId, quantity)

        return setTimeout(action, 150)
      })

    this.setState({ promise })
  }

  handleChangeQuantity = (e) => {
    let { value } = e.target

    if (value < 0) {
      value = 1
    }

    return this.setState({ quantity: value })
  }

  handleChangeVariation = (e) => {
    const id = e.target.value

    this.props.onChangeProductId && this.props.onChangeProductId(id)

    return this.setState({ selectedProductId: id })
  }

  setBundleConfiguration = (newBundleConfiguration) => {
    this.setState({ configuration: { products: newBundleConfiguration } })
  }

  loadBundlePriceAndAvailability = () => {
    const { cart, item } = this.props
    const { configuration } = this.state

    // Cart MUST be present. Otherwise we get wrong pricing displayed.
    if (!cart) return

    BundleActions.configurationAvailability({
      id: item.id,
      products: configuration.products,
      cart_id: cart.id,
      apiVersion: 3
    })
      .then((response) => {
        this.setState({
          price_each_in_cents: _get(response, 'payload.response.data.configuration.price_each_in_cents'),
          available_quantity: _get(response, 'payload.response.data.configuration.available_quantity')
        })
      })
      .catch(handleAxiosError(this.props.t))
  }

  renderAvailability = () => {
    const {
      useAvailability,
      showProductAvailability,
      availabilities,
      showCounts,
      hasDates,
      item,
      products,
      productGroup,
      bundle,
      cart,
      operatingRules,
      t
    } = this.props
    const { configuration } = this.state

    if (!useAvailability) return null

    let available
    const product = products && this.selectedProduct

    const withProductAvailability =
      useAvailability &&
      showProductAvailability &&
      ['bulk', 'trackable', 'mixed'].includes(item.tracking_type)

    if (typeof this.state.available_quantity === 'number') {
      available = this.state.available_quantity
    } else {
      available = (product || item || productGroup || bundle)?.available_quantity
    }

    return (
      <Fragment>
        {hasDates && available !== null && (
          <ReactCSSTransitionGroup transitionName="fade" transitionEnterTimeout={100} transitionLeaveTimeout={100}>
            {available > 0 && showCounts && (
              <span className="bq-branded bq-status bq-available">
                {!_get(productGroup, 'has_variations') && (
                  <span className="bq-status-quantity">
                    {available}
                    {' '}
                  </span>
                )}
                {t('user.store.available')?.toLowerCase?.()}
              </span>
            )}
            {available <= 0 && available !== -1 && (
              <span className="bq-status bq-unavailable">
                <span>{t('user.store.unavailable')}</span>
              </span>
            )}
            {(available === -1 ||
              (!showCounts && available > 0)) && (
              <span className="bq-branded bq-status bq-available">
                <span>{t('user.store.available')}</span>
              </span>
            )}
          </ReactCSSTransitionGroup>
        )}
        {withProductAvailability && (
          <ProductAvailabilityCalendar
            currentMonth={cart?.starts_at ? new Date(cart?.starts_at) : new Date()}
            availabilities={availabilities.getAll({ item_id: product?.id || item?.id })}
            defaultLocationId={cart?.start_location_id}
            id={product?.id || item?.id}
            productConfiguration={configuration}
            useBusinessHours
            operatingRules={operatingRules}
            showCounts={showCounts}
            selected={{ from: cart?.starts_at, to: cart?.stops_at }}
          />
        )}
      </Fragment>
    )
  }

  renderSelectVariation = () => {
    const { useAvailability, showCounts, hasDates, products, productGroup, t } = this.props
    const selectedProductId = this.state.selectedProductId || _get(this.selectedProduct, 'id')

    return (
      <div className="bq-variation-wrapper">
        <select
          name="variation"
          className="bq-variation"
          onChange={this.handleChangeVariation}
          value={selectedProductId}
        >
          {products.map((product) => {
            const withAvailability =
              useAvailability &&
              hasDates &&
              product.tracking_type !== 'none' &&
              product.available_quantity !== null &&
              showCounts

            return (
              <option key={product.id} value={product.id}>
                {withAvailability ?
                  `${product.name.replace(`${productGroup.name} - `, '')} (${
                    product.available_quantity
                  } ${t('user.store.available')})` :
                  product.name.replace(`${productGroup.name} - `, '')}
              </option>
            )
          })}
        </select>
        <Icon icon="angle-down" />
      </div>
    )
  }

  render () {
    const { item, productGroup, bundle, useAvailability, hasDates, behavior, t } = this.props
    const { quantity, configuration, price_each_in_cents: priceEachInCents } = this.state
    const selectedProduct = this.selectedProduct || bundle || item
    const loading = this.props.loading || this.state.loading
    const addButtonUsesCheckoutBehaviour = behavior === 'checkout'

    let disabled = quantity <= 0 || _isNaN(quantity)

    if (addButtonUsesCheckoutBehaviour && useAvailability && selectedProduct?.tracking_type !== 'none') {
      disabled =
        disabled ||
        (hasDates && !selectedProduct?.available_quantity) ||
        (hasDates && quantity > selectedProduct?.available_quantity)
    }

    return (
      <div className="booqable-product-button-wrapper" id={`booqable-product-button-${item && item.id}`}>
        {bundle && (
          <BundleItems
            item={item}
            useAvailability={useAvailability}
            hasDates={hasDates}
            configuration={configuration.products}
            setBundleConfiguration={this.setBundleConfiguration}
          />
        )}
        <div className="bq-actions">
          {_get(productGroup, 'has_variations') && this.renderSelectVariation()}
          <ProductPrice
            chargeLabel={selectedProduct.charge_label}
            priceInCents={priceEachInCents || selectedProduct.price_each_in_cents}
            priceType={selectedProduct.price_type}
            productType={selectedProduct.product_type}
            branded
          />
          {this.renderAvailability()}
          <div className="bq-control">
            <InputField
              field={{
                value: quantity,
                onChange: this.handleChangeQuantity,
                name: 'quantity'
              }}
              type="number"
              size="sm"
              className="bq-quantity"
            />
            <Spacer vertical size="xs" />
            <ReadOnlyModeDisabledComponent>
              <Button
                onClick={this.handleBook}
                promise={this.state.promise}
                size="sm"
                block
                disabled={loading || disabled}
                className={`bq-button ${disabled && 'bq-disabled'}`}
              >
                {t('user.store.add_to_cart')}
              </Button>
            </ReadOnlyModeDisabledComponent>
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state, props) => {
  const cart = state.orm.carts.getById(state.client.cartId)
  const productGroup = state.orm.product_groups.getById(props.item.id)

  const behavior = state.settings.get('store.behaviors.add_button')
  const hasDates = !!cart?.starts_at && !!cart?.stops_at

  return {
    cart,
    productGroup,
    operatingRules: getOperatingRules(state),
    availabilities: state.orm.shop_availabilities,
    useAvailability: state.settings.get('store.use_availability'),
    showProductAvailability: state.settings.get('store.show_product_availability'),
    bundle: state.orm.bundles.getById(props.item.id),
    products: state.orm.products.getAll({ product_group_id: productGroup && productGroup.id }).sort('sorting_weight'),
    showCounts: !state.settings.get('store.hide_product_availability_quantities'),
    behavior,
    hasDates
  }
}

const mapDispatchToProps = () => {
  return {
    clearCart: () => cartActions.clear(),
    book: (itemId, quantity, configuration) => {
      return cartActions.book({ item_id: itemId, quantity, configuration, apiVersion: 1 })
    }
  }
}

const reduxComponent = reduxConnect(mapStateToProps, mapDispatchToProps)(ProductButton)

export default withTranslation('user')(reduxComponent)
